import { useEffect, useState } from "react";
import api from "@/lib/api";
import { trackClick } from "@/lib/analytics";

function Stars({ rating = 5 }) {
  return (
    <div className="inline-flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i <= Math.round(rating) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeLinejoin="round" />
        </svg>
      ))}
    </div>
  );
}

export default function SocialProof({ variant = "full", className = "" }) {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/google-reviews").then((r) => { if (r.data && r.data.available) setLive(r.data); }).catch(() => {});
  }, []);

  if (!settings) return null;

  const ratingNum = Number(live?.rating || settings.google_rating || 0);
  const reviewCount = live?.review_count || settings.google_review_count || 0;
  const googleUrl = live?.google_maps_uri || settings.google_business_url || "#";
  const cards = live?.reviews?.length
    ? live.reviews.map((r, i) => ({ id: `g-${i}`, rating: r.rating, text: r.text, author: r.author, author_url: r.author_url, source: `Google · ${r.relative_time}` }))
    : reviews;

  // Compact variant — for sidebars/contact page
  if (variant === "compact") {
    return (
      <aside className={`bg-sand p-6 md:p-8 border-l-2 border-ember ${className}`} data-testid="social-proof-compact">
        <p className="font-mono-label text-ember mb-3">Verified guest reviews</p>
        <div className="flex items-baseline gap-3">
          <span className="font-display text-5xl text-ink leading-none">{ratingNum.toFixed(1)}</span>
          <div>
            <div className="text-ember"><Stars rating={ratingNum} /></div>
            <p className="font-mono-label text-ink-muted mt-1">{reviewCount} Google reviews</p>
          </div>
        </div>
        <a
          href={googleUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick("google:reviews")}
          className="mt-5 inline-block link-underline text-ink-soft font-mono-label hover:text-ember"
          data-testid="social-proof-google-link"
        >
          Read on Google →
        </a>
      </aside>
    );
  }

  // Full variant — hero strip for Home / About pages
  return (
    <section className={`bg-canvas py-20 md:py-28 border-y border-rule-subtle ${className}`} data-testid="social-proof-full">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4 md:sticky md:top-32 self-start">
          <p className="font-mono-label text-ember mb-4">Social proof</p>
          <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02]">
            {ratingNum.toFixed(1)} <span className="text-ember">★</span><br />
            <span className="italic text-3xl md:text-4xl">across {reviewCount}+ reviews.</span>
          </h2>
          <p className="mt-6 text-ink-soft leading-relaxed">
            Real guests, real reviews — pulled live from Google. We don't pay for placements, we don't
            filter the bad ones, and we read every single one.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("google:reviews-full")}
              className="btn-ink"
              data-testid="social-proof-google-cta"
            >
              All Google reviews
            </a>
            <a
              href={settings.google_business_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("google:write-review")}
              className="btn-ember"
              data-testid="social-proof-write-review"
            >
              Leave a review
            </a>
            {settings.tripadvisor_url && (
              <a href={settings.tripadvisor_url} target="_blank" rel="noopener noreferrer" className="font-mono-label text-ink-soft hover:text-ember link-underline self-center" onClick={() => trackClick("tripadvisor:reviews")}>
                Tripadvisor →
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-8">
          <div className="grid sm:grid-cols-2 gap-px bg-rule-strong">
            {cards.slice(0, 4).map((r, i) => (
              <article
                key={r.id}
                className="bg-canvas p-8 fade-up"
                style={{ animationDelay: `${i * 70}ms` }}
                data-testid={`review-card-${i}`}
              >
                <div className="text-ember mb-3"><Stars rating={r.rating} /></div>
                <p className="text-ink-soft leading-relaxed line-clamp-6">"{r.text}"</p>
                <p className="mt-5 font-mono-label text-ink-muted">
                  — {r.author_url && r.author_url.startsWith("https://") ? (
                    <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="hover:text-ember">{r.author}</a>
                  ) : r.author} · {r.source}
                </p>
              </article>
            ))}
          </div>
          {live && (
            <p className="mt-4 font-mono-label text-ink-muted" data-testid="google-attribution">
              Reviews from Google Maps
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
